var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { launchDocking as launchDockingAPI, checkDocking, stopDocking as stopDockingAPI, getSessionDockings, getSessionReceptors, getSessionPeptides, deleteSessionDocking, BACKEND_URL } from '../backendClient';
import { resolveCurrentSessionId, getSessionLoadGeneration, isSessionLoadCurrent } from '../sessionProjects';
import { WORKFLOW_LIST_LOADING_HTML, prepareWorkflowListForItems, } from '../workflowListLoading';
import { ensureInheritedTargetMarksHook, getMainTargetReceptorId, refreshInheritedTargetMarks, upsertInheritedTargetMark, } from '../targetRoleUi';
let dockingList = [];
let nextDockingID = 1;
// Function to parse DynamoDB poses format to normal objects
function parseDynamoDBPoses(rawPoses) {
    return rawPoses.map((pose) => {
        var _a, _b, _c;
        // Handle DynamoDB format: { "M": { "poseId": { "N": "1" }, "bindingAffinity": { "N": "-43.74" }, ... } }
        if (pose.M) {
            return {
                poseId: parseInt(((_a = pose.M.poseId) === null || _a === void 0 ? void 0 : _a.N) || pose.M.poseId || 0),
                bindingAffinity: parseFloat(((_b = pose.M.bindingAffinity) === null || _b === void 0 ? void 0 : _b.N) || pose.M.bindingAffinity || 0),
                posePdbPath: ((_c = pose.M.posePdbPath) === null || _c === void 0 ? void 0 : _c.S) || pose.M.posePdbPath || ''
            };
        }
        // Handle already parsed format
        return {
            poseId: parseInt(pose.poseId || 0),
            bindingAffinity: parseFloat(pose.bindingAffinity || 0),
            posePdbPath: pose.posePdbPath || ''
        };
    });
}
function broadcastDockingsState() {
    try {
        const anyProcessing = dockingList.some(d => d && (d.status === 'pending' || d.status === 'in_progress'));
        const referencedPeptideIDs = Array.from(new Set(dockingList.map(d => d.peptideID).filter(id => typeof id === 'number')));
        const referencedReceptorIDs = Array.from(new Set(dockingList.map(d => d.receptorID).filter(id => typeof id === 'number')));
        const evt = new CustomEvent('dockings-state', { detail: { anyProcessing, referencedPeptideIDs, referencedReceptorIDs } });
        window.dispatchEvent(evt);
    }
    catch (_a) { }
}
// Global state to track deletion in progress
let isDeletingDocking = false;
export function initDockingForm() {
    ensureInheritedTargetMarksHook();
    // Initialize docking form when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', setupDockingForm);
    }
    else {
        setupDockingForm();
    }
}
// Export function to load data after authentication
export function loadSessionDockings() {
    return loadExistingDockings().then(() => {
        // Ensure docking tab state is updated after loading
        updateBindingSiteButtonState();
        console.log('🔍 [DEBUG] loadSessionDockings completed, docking tab state updated');
    });
}
// Load existing dockings from database on initialization
function loadExistingDockings() {
    return __awaiter(this, void 0, void 0, function* () {
        var _a, _b, _c;
        const requestedSessionId = resolveCurrentSessionId();
        const loadGen = getSessionLoadGeneration();
        try {
            // Don't clear dockings if docking results modal is open to prevent tab disabling
            const dockingModal = document.getElementById('info-modal');
            const isDockingModalOpen = dockingModal && !dockingModal.classList.contains('hidden') && dockingModal.classList.contains('docking-results');
            if (!isDockingModalOpen) {
                // Quiet loading placeholder instead of an empty flash.
                dockingList.length = 0;
                const dockingContainer = document.getElementById('docking-list-container');
                if (dockingContainer) {
                    dockingContainer.innerHTML = WORKFLOW_LIST_LOADING_HTML;
                }
            }
            else {
                console.log('🔍 [DEBUG] Skipping docking list clear - modal is open');
                return Promise.resolve(); // Return resolved promise if modal is open
            }
            console.log('🔍 [DEBUG] Loading session data...');
            // Get all data in parallel
            const [dockingsResult, receptorsResult, peptidesResult] = yield Promise.all([
                getSessionDockings(),
                getSessionReceptors(),
                getSessionPeptides()
            ]);
            if (!isSessionLoadCurrent(loadGen, requestedSessionId))
                return;
            console.log('🔍 [DEBUG] Session data loaded - dockings:', ((_a = dockingsResult.dockings) === null || _a === void 0 ? void 0 : _a.length) || 0, 'receptors:', ((_b = receptorsResult.receptors) === null || _b === void 0 ? void 0 : _b.length) || 0, 'peptides:', ((_c = peptidesResult.peptides) === null || _c === void 0 ? void 0 : _c.length) || 0);
            const dockings = dockingsResult.dockings || [];
            const receptors = receptorsResult.receptors || [];
            const peptides = peptidesResult.peptides || [];
            // Don't reload receptors here - they are managed by receptorForm
            // Just use the data for lookups without triggering UI updates
            if (dockings.length > 0) {
                console.log('🔍 [DEBUG] Loading', dockings.length, 'dockings with poses data');
            }
            // Create lookup maps for quick access (normalize IDs — API may return string/number)
            const receptorMap = new Map(receptors.map(r => [Number(r.receptorID), r]));
            const peptideMap = new Map(peptides.map(p => [Number(p.peptideID), p]));
            // Add each existing docking to the form with complete data
            for (const dockingData of dockings) {
                const receptor = receptorMap.get(Number(dockingData.receptorID));
                const peptide = peptideMap.get(Number(dockingData.peptideID));
                // Parse DynamoDB poses format to normal objects
                const rawPoses = dockingData.poses || [];
                const parsedPoses = parseDynamoDBPoses(rawPoses);
                if (parsedPoses.length > 0) {
                    console.log(`[DEBUG] Docking ${dockingData.dockingID}: ${parsedPoses.length} poses loaded`);
                }
                // Generate backend docking ID (b prefix for both predefined and custom)
                let backendDockingID = dockingData.dockingID;
                if (backendDockingID && !backendDockingID.includes('-b')) {
                    const customBSId = dockingData.customBindingSiteId;
                    const bindingSiteValue = dockingData.bindingSiteID === -1
                        ? (customBSId || 'CUSTOM')
                        : String(dockingData.bindingSiteID);
                    backendDockingID = `${dockingData.receptorID}-${dockingData.peptideID}-b${bindingSiteValue}`;
                    console.log(`[DEBUG] Fixed backendDockingID from ${dockingData.dockingID} to ${backendDockingID}`);
                }
                // Debug custom binding site info
                if (dockingData.bindingSiteID === -1) {
                    console.log(`[DEBUG] Custom binding site docking:`, {
                        dockingID: dockingData.dockingID,
                        bindingSiteID: dockingData.bindingSiteID,
                        customBindingSiteId: dockingData.customBindingSiteId,
                        backendDockingID
                    });
                }
                // Ensure status is valid, default to 'pending' if not set or invalid
                let dockingStatus = dockingData.status || 'pending';
                if (!['pending', 'in_progress', 'completed', 'failed', 'job_submission_failed', 'cancelled'].includes(dockingStatus)) {
                    dockingStatus = 'pending';
                }
                console.log(`[DEBUG] Adding docking to form:`, {
                    receptorID: dockingData.receptorID,
                    peptideID: dockingData.peptideID,
                    bindingSiteID: dockingData.bindingSiteID,
                    customBindingSiteId: dockingData.customBindingSiteId,
                    backendDockingID
                });
                const dockingID = addDockingToForm(dockingData.receptorID, dockingData.peptideID, dockingData.bindingSiteID, dockingData.pdbID || (receptor === null || receptor === void 0 ? void 0 : receptor.pdbID) || 'Unknown', dockingData.peptideSequence || (peptide === null || peptide === void 0 ? void 0 : peptide.peptideSequence) || 'No sequence available', dockingStatus, backendDockingID, // Fixed backend docking ID
                parsedPoses, // Pass parsed poses data
                dockingData.customBindingSiteId // Pass custom binding site ID if present
                );
                // No auto-monitoring; refresh runs only on user action
            }
            // After loading, update delete buttons and broadcast state so other lists hide their crosses
            // IMPORTANT: Call updateBindingSiteButtonState AFTER all dockings are added
            updateBindingSiteButtonState();
            updateDockingDeleteButtons();
            updateDockingInfoButtons();
            broadcastDockingsState();
            refreshInheritedTargetMarks();
            // Update numbering based on position in list (sorted by creation date from backend)
            const container = document.getElementById('docking-list-container');
            if (container) {
                // Loading must never remain once we know the result (items or empty).
                prepareWorkflowListForItems(container);
                if (dockingList.length === 0) {
                    container.innerHTML = '<p class="no-dockings">No dockings performed yet</p>';
                }
                else {
                    const allDockingItems = container.querySelectorAll('.docking-item');
                    allDockingItems.forEach((item, index) => {
                        const numberSpan = item.querySelector('.peptide-number');
                        if (numberSpan)
                            numberSpan.textContent = `${index + 1}`;
                    });
                }
            }
        }
        catch (error) {
            console.error('Error loading existing dockings:', error);
            const container = document.getElementById('docking-list-container');
            if (container && container.querySelector('.workflow-list-loading')) {
                container.innerHTML = '<p class="no-dockings">No dockings performed yet</p>';
            }
        }
        finally {
            // Always update docking tab state, even if there was an error
            updateBindingSiteButtonState();
            console.log('🔍 [DEBUG] loadExistingDockings completed, docking tab state updated in finally block');
        }
    });
}
function setupDockingForm() {
    // Create docking list container if it doesn't exist
    const peptideTab = document.getElementById('peptide-tab');
    if (!peptideTab) {
        console.log(`❌ [DEBUG] setupDockingForm: peptide-tab not found`);
        return;
    }
    // Check if docking list already exists
    if (document.getElementById('docking-list-container')) {
        console.log(`🔍 [DEBUG] setupDockingForm: docking-list-container already exists`);
        return;
    }
    // Check if wizard header already exists
    if (document.getElementById('peptide-wizard-header')) {
        console.log(`🔍 [DEBUG] setupDockingForm: peptide-wizard-header already exists`);
        return;
    }
    console.log(`🔍 [DEBUG] setupDockingForm: Setting up docking form...`);
    // No wizard needed - docking results are now in their own tab
    // No wizard step handling needed - docking results are now in their own tab
    // Don't load data here - wait for authentication confirmation
    // loadExistingDockings(); // Moved to loadDockingDataAfterAuth()
    // Height handled purely via CSS (max-height = one item)
}
// Wizard step handling removed - docking results are now in their own tab
export function addDockingToForm(receptorID, peptideID, bindingSiteID, pdbID, peptideSequence, status = 'pending', backendDockingID, poses, customBindingSiteId // Custom binding site ID (A, B, C, ...) if bindingSiteID is -1
) {
    // Ensure docking form is initialized before adding items
    if (!document.getElementById('docking-list-container')) {
        setupDockingForm();
    }
    // New business rule: allow multiple dockings per (receptor, peptide) with different binding sites
    // Check if exact same docking (same receptor, peptide, AND binding site) already exists
    const existingDocking = dockingList.find(d => d.receptorID === receptorID &&
        d.peptideID === peptideID &&
        d.bindingSiteID === bindingSiteID);
    if (existingDocking) {
        // Exact same docking → preserve status if in progress/completed, only update if failed/pending/cancelled
        if (existingDocking.status === 'in_progress' || existingDocking.status === 'completed') {
            // Just select the existing docking, don't touch status or backend
            selectDocking(existingDocking);
            return existingDocking.dockingID;
        }
        else if (existingDocking.status === 'failed' || existingDocking.status === 'job_submission_failed' || existingDocking.status === 'pending' || existingDocking.status === 'cancelled') {
            // Only update if failed, job_submission_failed, pending, or cancelled - this will trigger a new backend call
            existingDocking.status = status;
            existingDocking.pdbID = pdbID;
            existingDocking.peptideSequence = peptideSequence;
            existingDocking.statusMessage = '';
            existingDocking.resultPath = undefined;
            updateDockingElement(existingDocking);
            selectDocking(existingDocking);
            return existingDocking.dockingID;
        }
    }
    // If no exact match found, create a new docking (even if same receptor/peptide but different binding site)
    const dockingID = `docking_${nextDockingID++}`;
    // Generate backendDockingID with binding site if not provided (b prefix for both predefined and custom)
    const bindingSiteValue = (bindingSiteID === -1 && customBindingSiteId)
        ? customBindingSiteId
        : String(bindingSiteID);
    const generatedBackendDockingID = backendDockingID || `${receptorID}-${peptideID}-b${bindingSiteValue}`;
    console.log(`[DEBUG] Generated backendDockingID: ${generatedBackendDockingID} for receptorID: ${receptorID}, peptideID: ${peptideID}, bindingSiteID: ${bindingSiteID}`);
    const dockingItem = Object.assign(Object.assign({ dockingID,
        receptorID,
        peptideID,
        bindingSiteID }, (customBindingSiteId && { customBindingSiteId })), { // Add custom binding site ID if present
        pdbID,
        peptideSequence, status: status, backendDockingID: generatedBackendDockingID, poses: poses || [] // Add poses data
     });
    dockingList.push(dockingItem);
    console.log(`[DEBUG] Added docking to list. Total dockings: ${dockingList.length}, status: ${dockingItem.status}`);
    // Create and add element to UI
    const element = createDockingElement(dockingItem);
    dockingItem.element = element;
    // Immediately update button visibility based on status
    const stopBtn = element.querySelector('.btn-stop');
    if (stopBtn) {
        if (dockingItem.status === 'in_progress' || dockingItem.status === 'pending') {
            stopBtn.style.display = 'flex';
            stopBtn.style.visibility = 'visible';
            console.log(`[DEBUG] Stop button immediately shown for new docking ${dockingItem.dockingID}`);
        }
        else {
            stopBtn.classList.remove('btn-visible');
            stopBtn.classList.add('btn-hidden');
        }
    }
    const container = document.getElementById('docking-list-container');
    if (container) {
        prepareWorkflowListForItems(container);
        container.appendChild(element);
        // Update numbering based on position in list (sorted by creation date from backend)
        const allDockingItems = container.querySelectorAll('.docking-item');
        allDockingItems.forEach((item, index) => {
            const numberSpan = item.querySelector('.peptide-number');
            if (numberSpan)
                numberSpan.textContent = `${index + 1}`;
        });
    }
    // Show toast for first launch (success - green, only for NEW dockings, not when loading from session)
    // Only show toast if this is a new docking (no backendDockingID provided) and status is pending/in_progress
    if (!backendDockingID && (status === 'pending' || status === 'in_progress')) {
        const showToast = window.showToast;
        if (showToast) {
            showToast('Docking launched.', 'success', 4000);
        }
        // Mark element as just launched to prevent "still in progress" toast on first refresh
        if (dockingItem.element) {
            dockingItem.element.setAttribute('data-just-launched', 'true');
        }
    }
    // Update element to ensure buttons visibility is correct
    updateDockingElement(dockingItem);
    // Update binding site button state AFTER adding to DOM
    // This ensures the docking tab is enabled when dockings exist
    updateBindingSiteButtonState();
    broadcastDockingsState();
    // Start monitoring docking status only for new dockings (not loaded from database)
    if (!backendDockingID) {
        refreshDockingStatus(dockingItem);
        // Remove the flag after first refresh
        if (dockingItem.element) {
            dockingItem.element.removeAttribute('data-just-launched');
        }
    }
    // If this is the first docking and it's newly launched (not loaded from backend),
    // automatically switch to step 2. Do NOT auto-switch on page load to keep focus on peptide list.
    if (!backendDockingID && dockingList.length === 1) {
        const step1Btn = document.getElementById('peptide-step-1-btn');
        const step2Btn = document.getElementById('peptide-step-2-btn');
        const step2 = document.getElementById('peptide-step-2');
        if (step1Btn && step2Btn && step2) {
            step1Btn.classList.remove('active');
            step2Btn.classList.add('active');
            // Hide peptide content
            const peptideInputContainer = document.getElementById('peptide-input-container');
            const peptideDetails = document.getElementById('peptide-details');
            const peptideListContainer = document.getElementById('peptide-list-container');
            if (peptideInputContainer)
                peptideInputContainer.style.display = 'none';
            if (peptideDetails)
                peptideDetails.style.display = 'none';
            if (peptideListContainer)
                peptideListContainer.style.display = 'none';
            // Show docking step
            step2.classList.remove('hidden');
        }
    }
    return dockingID;
}
function createDockingElement(dockingItem) {
    const element = document.createElement('div');
    element.className = 'docking-item';
    element.dataset.dockingId = dockingItem.dockingID;
    element.setAttribute('data-receptor-id', String(dockingItem.receptorID));
    element.setAttribute('data-peptide-id', String(dockingItem.peptideID));
    if (dockingItem.backendDockingID) {
        element.setAttribute('data-backend-docking-id', dockingItem.backendDockingID);
    }
    const { label: statusLabel, cls: statusClass } = getPeptideLikeStatus(dockingItem.status, dockingItem.statusMessage);
    // Show check button only when job is pending or in_progress
    const showCheckBtn = dockingItem.status === 'pending' || dockingItem.status === 'in_progress';
    const formattedSequence = formatPeptideSequenceForDockingDisplay(dockingItem.peptideSequence || 'No sequence available');
    element.innerHTML = `
    <div class="docking-item-header">
      <div class="docking-left">
        <span class="peptide-number">0</span>
        <span class="peptide-status ${statusClass}">${statusLabel}</span>
      </div>
      <div class="peptide-controls">
        <button class="btn-check btn-icon ${!showCheckBtn ? 'btn-hidden' : ''}" title="Refresh status">
          <svg class="check-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/>
            <path d="M21 3v5h-5"/>
            <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/>
            <path d="M3 21v-5h5"/>
          </svg>
        </button>
        <button class="btn-restart btn-icon ${dockingItem.status !== 'cancelled' ? 'btn-hidden' : ''}" title="Restart docking">
          <svg class="restart-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M1 4v6h6"/>
            <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/>
          </svg>
        </button>
        <button class="btn-stop btn-icon" title="Stop docking">
          <svg class="stop-icon" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10" fill="currentColor"/>
            <rect x="8" y="8" width="8" height="8" fill="white" rx="1"/>
          </svg>
        </button>
        <button class="btn-info btn-icon ${dockingItem.status !== 'completed' ? 'btn-hidden' : ''}" title="View docking details">
          <svg class="info-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <path d="M12 16v-4"/>
            <path d="M12 8h.01"/>
          </svg>
        </button>
        <button class="btn-download btn-icon ${dockingItem.status !== 'completed' ? 'btn-hidden' : ''}" title="Download PDB poses">
          <svg class="download-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="7,10 12,15 17,10"/>
            <line x1="12" y1="15" x2="12" y2="3"/>
          </svg>
        </button>
        <button class="btn-delete btn-icon" title="Clear docking">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
    </div>
    <div class="docking-meta"><span class="chip chip-rec">Receptor: ${dockingItem.pdbID || 'Unknown'}</span><span class="chip chip-site">Binding Site: ${(() => {
        const result = dockingItem.bindingSiteID === -1 ? (dockingItem.customBindingSiteId || 'Custom') : dockingItem.bindingSiteID;
        if (dockingItem.bindingSiteID === -1) {
            console.log(`[DEBUG] Custom binding site display:`, { bindingSiteID: dockingItem.bindingSiteID, customBindingSiteId: dockingItem.customBindingSiteId, result });
        }
        return result;
    })()}</span></div>
    <div class="docking-seq">${formattedSequence}</div>
    <div class="docking-progress-container btn-hidden" id="progress-container-${dockingItem.dockingID}">
      <div class="docking-progress-bar">
        <div class="docking-progress-fill" id="progress-fill-${dockingItem.dockingID}"></div>
        <div class="docking-progress-text" id="progress-text-${dockingItem.dockingID}"></div>
      </div>
    </div>
    <div class="docking-status-message" id="status-message-${dockingItem.dockingID}"></div>
  `;
    const mainRid = getMainTargetReceptorId();
    if (mainRid != null && Number(dockingItem.receptorID) === mainRid) {
        upsertInheritedTargetMark(element, true, 'Docking on session Target');
    }
    // Add event listeners
    const refreshBtn = element.querySelector('.btn-check');
    const restartBtn = element.querySelector('.btn-restart');
    const stopBtn = element.querySelector('.btn-stop');
    const infoBtn = element.querySelector('.btn-info');
    const downloadBtn = element.querySelector('.btn-download');
    const deleteBtn = element.querySelector('.btn-delete');
    // Hide check when completed, failed, or cancelled
    if (dockingItem.status === 'completed' || dockingItem.status === 'failed' || dockingItem.status === 'job_submission_failed' || dockingItem.status === 'cancelled') {
        refreshBtn.classList.remove('btn-visible');
        refreshBtn.classList.add('btn-hidden');
    }
    refreshBtn.addEventListener('click', (ev) => {
        ev.stopPropagation();
        // Prevent double-click by hiding button immediately
        if (refreshBtn.classList.contains('btn-hidden'))
            return;
        refreshBtn.classList.remove('btn-visible');
        refreshBtn.classList.add('btn-hidden');
        // Find docking item from list to ensure we have the latest data
        const currentDockingItem = dockingList.find(d => d.dockingID === dockingItem.dockingID);
        if (currentDockingItem) {
            refreshDockingStatus(currentDockingItem).finally(() => {
                if (currentDockingItem.status === 'pending' || currentDockingItem.status === 'in_progress') {
                    refreshBtn.classList.remove('btn-hidden');
                    refreshBtn.classList.add('btn-visible');
                }
            });
        }
        else {
            if (dockingItem.status === 'pending' || dockingItem.status === 'in_progress') {
                refreshBtn.classList.remove('btn-hidden');
                refreshBtn.classList.add('btn-visible');
            }
        }
    });
    // Restart button event listener 
    if (restartBtn) {
        restartBtn.addEventListener('click', (ev) => __awaiter(this, void 0, void 0, function* () {
            ev.stopPropagation();
            // Find docking item from list to ensure we have the latest data
            const currentDockingItem = dockingList.find(d => d.dockingID === dockingItem.dockingID);
            if (currentDockingItem) {
                yield restartDocking(currentDockingItem, element);
            }
            else {
                console.warn('[DEBUG] Cannot restart docking: docking item not found');
                const showToast = window.showToast;
                if (showToast) {
                    showToast('Cannot restart docking: docking not found. Please try again.', 'error', 4000);
                }
            }
        }));
    }
    // Stop button event listener
    if (stopBtn) {
        stopBtn.addEventListener('click', (ev) => __awaiter(this, void 0, void 0, function* () {
            ev.stopPropagation();
            // Read backendDockingID from data attribute to ensure we have the latest value
            const backendDockingID = element.getAttribute('data-backend-docking-id');
            if (backendDockingID) {
                // Find docking item from list to ensure we have the latest data
                const currentDockingItem = dockingList.find(d => d.dockingID === dockingItem.dockingID);
                if (currentDockingItem) {
                    yield stopDocking(backendDockingID, currentDockingItem);
                }
            }
            else {
                console.warn('[DEBUG] Cannot stop docking: backendDockingID not available');
                const showToast = window.showToast;
                if (showToast) {
                    showToast('Cannot stop docking: docking ID not yet available. Please wait a moment and try again.', 'warning', 4000);
                }
            }
        }));
    }
    // Info button event listener
    infoBtn.addEventListener('click', (ev) => {
        ev.stopPropagation();
        ev.preventDefault();
        if (dockingItem.status === 'completed' && !infoBtn.disabled) {
            // Select the docking item before showing the modal
            console.log('[DEBUG] Info button clicked, selecting docking:', dockingItem.dockingID);
            selectDocking(dockingItem);
            // Small delay to ensure selection is applied before modal
            setTimeout(() => {
                showDockingInfoModal(dockingItem);
            }, 10);
        }
    });
    // Download button event listener
    downloadBtn.addEventListener('click', (ev) => {
        ev.stopPropagation();
        if (dockingItem.status === 'completed' && !downloadBtn.disabled) {
            downloadDockingPDB(dockingItem);
        }
    });
    deleteBtn.addEventListener('click', (ev) => __awaiter(this, void 0, void 0, function* () {
        ev.stopPropagation();
        // Prevent double-click by disabling button immediately
        if (deleteBtn.disabled)
            return;
        // Show confirmation modal
        const showConfirm = window.showConfirmationModal;
        if (!showConfirm) {
            console.error('showConfirmationModal not available');
            return;
        }
        const confirmed = yield showConfirm('Delete Docking', `Are you sure you want to delete the docking result for receptor "${dockingItem.pdbID}" and peptide "${dockingItem.peptideSequence}"? It will be permanently removed from your docking results list and cannot be restored.`, 'Delete', 'Cancel');
        if (!confirmed) {
            return;
        }
        deleteBtn.disabled = true;
        deleteBtn.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>';
        // Read backendDockingID from data attribute to ensure we have the latest value
        const backendDockingID = element.getAttribute('data-backend-docking-id') || undefined;
        deleteDocking(dockingItem.dockingID, backendDockingID).finally(() => {
            // Re-enable button after completion
            deleteBtn.disabled = false;
            deleteBtn.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>';
        });
    }));
    // Always clickable - show receptor during processing, results when completed
    element.classList.add('clickable');
    // Enable selection to view results
    element.addEventListener('click', (e) => {
        console.log('[DEBUG] Docking element clicked:', dockingItem.dockingID);
        console.log('[DEBUG] Event target:', e.target);
        console.log('[DEBUG] Event currentTarget:', e.currentTarget);
        selectDocking(dockingItem);
    });
    updateDockingDeleteButtons();
    updateDockingInfoButtons();
    updateDockingElement(dockingItem);
    return element;
}
function formatPeptideSequenceForDockingDisplay(sequence) {
    return sequence
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '')
        .replace(/NH2/g, 'NH₂');
}
function getStatusText(status) {
    switch (status) {
        case 'pending': return 'Pending';
        case 'in_progress': return 'In Progress';
        case 'completed': return 'Completed';
        case 'failed': return 'Failed';
        case 'job_submission_failed': return 'Job Submission Failed';
        case 'cancelled': return 'Cancelled';
        default: return 'Unknown';
    }
}
function getPeptideLikeStatus(status, message) {
    // Header philosophy:
    // - Always keep header compact. During running, never inject the long message.
    // - If there is a message while running, hint user to see details below.
    if (status === 'completed')
        return { label: '✔ Ready', cls: 'ready' };
    if (status === 'job_submission_failed' || status === 'failed')
        return { label: '❌ Error', cls: 'error' };
    if (status === 'cancelled')
        return { label: '🚫 Cancelled', cls: 'error' };
    if (status === 'in_progress') {
        return {
            label: '⏳ Processing...',
            cls: 'processing'
        };
    }
    if (status === 'pending')
        return { label: '⏳ Pending...', cls: 'pending' };
    return { label: '⏳ Processing...', cls: 'processing' };
}
function refreshDockingStatus(dockingItem) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j;
        // Get previous status to detect changes (capture BEFORE any status checks)
        const previousStatus = dockingItem.status || 'pending';
        // Check if this is the first refresh after launch (element has data-just-launched attribute)
        const isFirstRefresh = ((_a = dockingItem.element) === null || _a === void 0 ? void 0 : _a.hasAttribute('data-just-launched')) || false;
        // If docking is already completed, reload data to ensure we have latest results
        if (dockingItem.status === 'completed') {
            console.log('[DEBUG] Docking already completed, reloading data to ensure latest results');
            yield reloadDockingData(dockingItem);
            return;
        }
        // Skip refresh if already in final state (failed, job_submission_failed, or cancelled)
        if (dockingItem.status === 'failed' || dockingItem.status === 'job_submission_failed' || dockingItem.status === 'cancelled') {
            return;
        }
        // Update status to in_progress
        updateDockingStatus(dockingItem.dockingID, 'in_progress');
        // Single-shot status check using backendClient function
        try {
            const result = yield checkDocking(dockingItem.receptorID, dockingItem.peptideID, dockingItem.bindingSiteID, dockingItem.customBindingSiteId);
            const dockingDone = result.dockingDone;
            let dockingStatus = result.dockingStatus;
            const statusMessage = result.statusMessage || '';
            const poseCount = result.poseCount || 0;
            // Check if statusMessage indicates an error even if status is still "in_progress"
            // This can happen when DynamoDB hasn't been updated yet but JSON file shows error
            const errorIndicators = ['error', 'failed', 'failure', 'exception', 'killed', 'terminated'];
            const hasErrorMessage = statusMessage && errorIndicators.some(indicator => statusMessage.toLowerCase().includes(indicator));
            // If we have an error message but status is still in_progress, treat it as failed
            if (hasErrorMessage && (dockingStatus === 'in_progress' || dockingStatus === 'pending')) {
                console.log(`[DEBUG] Detected error in statusMessage but status is ${dockingStatus}, treating as failed`);
                // Check if it's a job submission failure based on message content
                if (statusMessage.toLowerCase().includes('job submission') || statusMessage.toLowerCase().includes('submission failed')) {
                    dockingStatus = 'job_submission_failed';
                }
                else {
                    dockingStatus = 'failed';
                }
            }
            if (dockingDone) {
                dockingItem.statusMessage = '';
                updateDockingStatus(dockingItem.dockingID, 'completed');
                // Auto-select the docking when it becomes completed
                selectDocking(dockingItem);
                // Automatically load results in Mol* when docking completes
                viewDockingResult(dockingItem);
                // Reload docking data from backend to get updated poses (like receptors/peptides, we use data from checkDocking response)
                // Only reload poses data, don't overwrite status since we just set it to completed
                console.log('[DEBUG] Docking completed, reloading poses data from backend');
                try {
                    const dockingsResult = yield getSessionDockings();
                    const dockings = dockingsResult.dockings || [];
                    const updatedDocking = dockings.find((d) => d.receptorID === dockingItem.receptorID &&
                        d.peptideID === dockingItem.peptideID &&
                        d.bindingSiteID === dockingItem.bindingSiteID);
                    if (updatedDocking) {
                        if (updatedDocking.poses && Array.isArray(updatedDocking.poses)) {
                            dockingItem.poses = updatedDocking.poses;
                            console.log('[DEBUG] Updated poses count:', dockingItem.poses.length);
                        }
                        dockingItem.backendDockingID = updatedDocking.dockingID;
                        dockingItem.status = 'completed';
                        updateDockingElement(dockingItem);
                        updateDockingInfoButtons();
                    }
                }
                catch (error) {
                    console.error('[DEBUG] Error reloading docking poses:', error);
                }
                // Show toast if status changed to completed (but not during stop or bulk refresh)
                if (previousStatus !== 'completed' && !((_b = dockingItem.element) === null || _b === void 0 ? void 0 : _b.hasAttribute('data-stopping')) && !((_c = dockingItem.element) === null || _c === void 0 ? void 0 : _c.hasAttribute('data-refreshing-all'))) {
                    const showToast = window.showToast;
                    if (showToast) {
                        showToast('Docking is now ready.', 'success', 4000);
                    }
                }
            }
            else if (dockingStatus === 'failed' || dockingStatus === 'job_submission_failed') {
                // Distinguish between job submission failure and execution failure
                if (dockingStatus === 'job_submission_failed') {
                    dockingItem.statusMessage = statusMessage || 'Job submission failed';
                    updateDockingStatus(dockingItem.dockingID, 'job_submission_failed');
                }
                else {
                    dockingItem.statusMessage = statusMessage || 'Docking failed';
                    updateDockingStatus(dockingItem.dockingID, 'failed');
                }
                // Hide check button immediately when failed
                if (dockingItem.element) {
                    const checkBtn = dockingItem.element.querySelector('.btn-check');
                    if (checkBtn) {
                        checkBtn.classList.remove('btn-visible');
                        checkBtn.classList.add('btn-hidden');
                    }
                }
                // Show toast if status changed to failed (but not during stop or bulk refresh)
                if (previousStatus !== 'failed' && previousStatus !== 'job_submission_failed' && !((_d = dockingItem.element) === null || _d === void 0 ? void 0 : _d.hasAttribute('data-stopping')) && !((_e = dockingItem.element) === null || _e === void 0 ? void 0 : _e.hasAttribute('data-refreshing-all'))) {
                    const showToast = window.showToast;
                    if (showToast) {
                        const toastMessage = dockingStatus === 'job_submission_failed'
                            ? 'Docking job submission failed.'
                            : 'Docking has failed.';
                        showToast(toastMessage, 'error', 4000);
                    }
                }
            }
            else if (dockingStatus === 'cancelled') {
                dockingItem.statusMessage = statusMessage || 'Docking cancelled';
                dockingItem.status = 'cancelled';
                // Update status immediately in UI (like receptor)
                if (dockingItem.element) {
                    const statusSpan = dockingItem.element.querySelector('.peptide-status');
                    if (statusSpan) {
                        statusSpan.textContent = '🚫 Cancelled';
                        statusSpan.className = 'peptide-status error';
                    }
                }
                // Hide check and stop buttons when cancelled
                if (dockingItem.element) {
                    const checkBtn = dockingItem.element.querySelector('.btn-check');
                    if (checkBtn) {
                        checkBtn.classList.remove('btn-visible');
                        checkBtn.classList.add('btn-hidden');
                    }
                    const stopBtn = dockingItem.element.querySelector('.btn-stop');
                    if (stopBtn) {
                        stopBtn.classList.remove('btn-visible');
                        stopBtn.classList.add('btn-hidden');
                    }
                }
                // Update the entire element
                updateDockingStatus(dockingItem.dockingID, 'cancelled');
            }
            else if (dockingStatus === 'in_progress') {
                // Use the detailed status message from backend
                dockingItem.statusMessage = statusMessage || 'Docking in progress...';
                updateDockingStatus(dockingItem.dockingID, 'in_progress');
                // Always update progress bar when in progress (it will show/hide based on message content)
                updateDockingProgressBar(dockingItem);
                // Show toast if still processing (but not on first refresh after launch or stop, or during bulk refresh)
                if ((previousStatus === 'in_progress' || previousStatus === 'pending') && !isFirstRefresh && !((_f = dockingItem.element) === null || _f === void 0 ? void 0 : _f.hasAttribute('data-stopping')) && !((_g = dockingItem.element) === null || _g === void 0 ? void 0 : _g.hasAttribute('data-refreshing-all'))) {
                    const showToast = window.showToast;
                    if (showToast) {
                        showToast('Docking is still in progress.', 'info', 4000);
                    }
                }
            }
            else {
                // Default to pending if status is unclear
                dockingItem.statusMessage = statusMessage || 'Docking pending...';
                updateDockingStatus(dockingItem.dockingID, 'pending');
                // Show toast if still pending (but not on first refresh after launch or stop, or during bulk refresh)
                if ((previousStatus === 'pending' || previousStatus === 'in_progress') && !isFirstRefresh && !((_h = dockingItem.element) === null || _h === void 0 ? void 0 : _h.hasAttribute('data-stopping')) && !((_j = dockingItem.element) === null || _j === void 0 ? void 0 : _j.hasAttribute('data-refreshing-all'))) {
                    const showToast = window.showToast;
                    if (showToast) {
                        showToast('Docking is still in progress.', 'info', 4000);
                    }
                }
            }
        }
        catch (error) {
            console.error('Error checking docking status:', error);
            // Check if it's an authentication error
            if (error instanceof Error && error.message.includes('Authentication failed')) {
                console.warn('🔒 Authentication failed during docking status check - user will be logged out');
                // Don't update status to failed for auth errors, just log
                return;
            }
            // Only update to failed for actual errors, not auth issues
            dockingItem.statusMessage = 'Error while checking status';
            updateDockingStatus(dockingItem.dockingID, 'failed');
        }
    });
}
export function updateDockingStatus(dockingID, status) {
    const dockingItem = dockingList.find(item => item.dockingID === dockingID);
    if (dockingItem) {
        dockingItem.status = status;
        updateDockingElement(dockingItem);
        updateDockingDeleteButtons();
        updateDockingInfoButtons();
        broadcastDockingsState();
        // If this docking is currently selected and just completed, load its results
        if (status === 'completed' && dockingItem.element && dockingItem.element.classList.contains('selected')) {
            viewDockingResult(dockingItem);
        }
    }
}
function updateDockingProgressBar(dockingItem) {
    const progressContainer = document.getElementById(`progress-container-${dockingItem.dockingID}`);
    const progressFill = document.getElementById(`progress-fill-${dockingItem.dockingID}`);
    const progressText = document.getElementById(`progress-text-${dockingItem.dockingID}`);
    if (!progressContainer || !progressFill || !progressText)
        return;
    // Only show progress bar when we have pose information
    if (dockingItem.statusMessage) {
        const message = dockingItem.statusMessage;
        // Extract progress from message like "Done 27 poses out of 40" or "27 poses out of 40"
        const progressMatch = message.match(/(\d+)\s+poses?\s+out\s+of\s+(\d+)/i);
        if (progressMatch) {
            const current = parseInt(progressMatch[1]);
            const total = parseInt(progressMatch[2]);
            const percentage = Math.round((current / total) * 100);
            // Update progress bar
            progressFill.style.width = `${percentage}%`;
            progressText.textContent = `${percentage}%`;
            // White text with thin green outline to match scrollbar color
            progressText.style.color = 'white';
            progressText.style.textShadow = '1px 0px 0px #1c9b6d, -1px 0px 0px #1c9b6d, 0px 1px 0px #1c9b6d, 0px -1px 0px #1c9b6d';
            progressText.style.fontWeight = 'bold';
            // Show progress container (remove btn-hidden class and set display)
            progressContainer.classList.remove('btn-hidden');
            progressContainer.style.display = 'block';
        }
        else {
            // Hide progress container if no pose info
            progressContainer.classList.add('btn-hidden');
            progressContainer.style.display = 'none';
        }
    }
    else {
        // Hide progress container if no message
        progressContainer.classList.add('btn-hidden');
        progressContainer.style.display = 'none';
    }
}
export function updateDockingElement(dockingItem) {
    if (!dockingItem.element)
        return;
    // Update data-backend-docking-id attribute if backendDockingID is available
    if (dockingItem.backendDockingID) {
        dockingItem.element.setAttribute('data-backend-docking-id', dockingItem.backendDockingID);
    }
    const statusElement = dockingItem.element.querySelector('.peptide-status');
    const { label: statusLabel, cls: statusClass } = getPeptideLikeStatus(dockingItem.status, dockingItem.statusMessage);
    if (statusElement) {
        statusElement.className = `peptide-status ${statusClass}`;
        // keep to single line and shrink font to fit available space
        statusElement.textContent = statusLabel;
        const el = statusElement;
        el.style.whiteSpace = 'nowrap';
        el.style.overflow = 'hidden';
        el.style.textOverflow = 'clip';
        autoFitTextWidth(el, 9, 13);
    }
    // Update progress bar
    updateDockingProgressBar(dockingItem);
    // Update and auto-fit the detailed status message block
    const detailEl = dockingItem.element.querySelector(`#status-message-${dockingItem.dockingID}`);
    if (detailEl) {
        const msg = dockingItem.status === 'completed' ? '' : (dockingItem.statusMessage || '');
        detailEl.textContent = msg;
        detailEl.style.display = msg ? 'block' : 'none';
        if (msg) {
            // Force consistent small font size for all status messages
            detailEl.style.fontSize = '10px';
            detailEl.style.lineHeight = '1.2';
        }
    }
    // Always clickable - show receptor during processing, results when completed
    if (dockingItem.element) {
        dockingItem.element.classList.add('clickable');
        dockingItem.element.classList.remove('not-clickable');
    }
    // Toggle buttons visibility by status - use display:none to avoid empty spaces
    const checkBtn = dockingItem.element.querySelector('.btn-check');
    const restartBtn = dockingItem.element.querySelector('.btn-restart');
    const infoBtn = dockingItem.element.querySelector('.btn-info');
    const downloadBtn = dockingItem.element.querySelector('.btn-download');
    if (checkBtn) {
        if (dockingItem.status === 'pending' || dockingItem.status === 'in_progress') {
            checkBtn.classList.remove('btn-hidden');
            checkBtn.classList.add('btn-visible');
        }
        else {
            checkBtn.classList.remove('btn-visible');
            checkBtn.classList.add('btn-hidden');
        }
    }
    if (restartBtn) {
        // Restart button: only show when cancelled
        if (dockingItem.status === 'cancelled') {
            restartBtn.classList.remove('btn-hidden');
            restartBtn.classList.add('btn-visible');
        }
        else {
            restartBtn.classList.remove('btn-visible');
            restartBtn.classList.add('btn-hidden');
        }
    }
    const stopBtn = dockingItem.element.querySelector('.btn-stop');
    if (stopBtn) {
        // Stop button: only show during processing/pending
        if (dockingItem.status === 'in_progress' || dockingItem.status === 'pending') {
            stopBtn.classList.remove('btn-hidden');
            stopBtn.classList.add('btn-visible');
            console.log(`[DEBUG] Stop button shown for docking ${dockingItem.dockingID} with status ${dockingItem.status}`);
        }
        else {
            stopBtn.classList.remove('btn-visible');
            stopBtn.classList.add('btn-hidden');
            console.log(`[DEBUG] Stop button hidden for docking ${dockingItem.dockingID} with status ${dockingItem.status}`);
        }
    }
    else {
        console.error(`[DEBUG] Stop button not found for docking ${dockingItem.dockingID}`);
    }
    if (infoBtn) {
        // Info button: only show when completed
        if (dockingItem.status === 'completed') {
            infoBtn.classList.remove('btn-hidden');
            infoBtn.classList.add('btn-visible');
            infoBtn.style.display = 'flex';
        }
        else {
            infoBtn.classList.remove('btn-visible');
            infoBtn.classList.add('btn-hidden');
            infoBtn.style.display = 'none';
        }
    }
    if (downloadBtn) {
        // Download button: only show when completed
        if (dockingItem.status === 'completed') {
            downloadBtn.classList.remove('btn-hidden');
            downloadBtn.classList.add('btn-visible');
            downloadBtn.style.display = 'flex';
        }
        else {
            downloadBtn.classList.remove('btn-visible');
            downloadBtn.classList.add('btn-hidden');
            downloadBtn.style.display = 'none';
        }
    }
}
function updateDockingDeleteButtons() {
    const container = document.getElementById('docking-list-container');
    if (!container)
        return;
    const items = Array.from(container.querySelectorAll('.docking-item'));
    const anyProcessing = items.some(it => {
        const st = it.querySelector('.peptide-status');
        return st && (st.classList.contains('processing') || st.classList.contains('pending'));
    });
    // If any docking is processing (but allow cancelled) OR if we're currently deleting, hide all delete buttons
    const anyProcessingNotCancelled = items.some(it => {
        var _a;
        const st = it.querySelector('.peptide-status');
        const isProcessing = st && (st.classList.contains('processing') || st.classList.contains('pending'));
        const isCancelled = (_a = st === null || st === void 0 ? void 0 : st.textContent) === null || _a === void 0 ? void 0 : _a.includes('Cancelled');
        const result = isProcessing && !isCancelled;
        if (result) {
            console.log(`[DEBUG] Docking is processing:`, { text: st === null || st === void 0 ? void 0 : st.textContent, classList: Array.from((st === null || st === void 0 ? void 0 : st.classList) || []) });
        }
        return result;
    });
    console.log(`[DEBUG] updateDockingDeleteButtons: anyProcessingNotCancelled=${anyProcessingNotCancelled}, isDeletingDocking=${isDeletingDocking}`);
    if (anyProcessingNotCancelled || isDeletingDocking) {
        items.forEach(it => {
            const del = it.querySelector('.btn-delete');
            if (del) {
                del.style.display = 'none';
                del.style.visibility = 'hidden';
                del.disabled = true;
            }
        });
    }
    else {
        // If no dockings are processing and no deletion in progress, show all delete buttons
        items.forEach(it => {
            const del = it.querySelector('.btn-delete');
            if (del) {
                del.style.display = '';
                del.style.visibility = 'visible';
                del.disabled = false;
            }
        });
    }
    // notify receptor & peptide lists to update their delete buttons
    window.dispatchEvent(new Event('dockings-updated'));
}
function updateDockingInfoButtons() {
    const container = document.getElementById('docking-list-container');
    if (!container)
        return;
    const items = Array.from(container.querySelectorAll('.docking-item'));
    items.forEach(it => {
        const infoBtn = it.querySelector('.btn-info');
        const downloadBtn = it.querySelector('.btn-download');
        if (infoBtn || downloadBtn) {
            const dockingIdAttr = it.getAttribute('data-docking-id');
            if (dockingIdAttr) {
                const dockingItem = dockingList.find(d => d.dockingID === dockingIdAttr);
                if (dockingItem) {
                    if (dockingItem.status === 'completed') {
                        if (infoBtn) {
                            infoBtn.classList.remove('btn-hidden');
                            infoBtn.classList.add('btn-visible');
                            infoBtn.style.display = 'flex';
                        }
                        if (downloadBtn) {
                            downloadBtn.classList.remove('btn-hidden');
                            downloadBtn.classList.add('btn-visible');
                            downloadBtn.style.display = 'flex';
                        }
                    }
                    else {
                        if (infoBtn) {
                            infoBtn.classList.remove('btn-visible');
                            infoBtn.classList.add('btn-hidden');
                            infoBtn.style.display = 'none';
                        }
                        if (downloadBtn) {
                            downloadBtn.classList.remove('btn-visible');
                            downloadBtn.classList.add('btn-hidden');
                            downloadBtn.style.display = 'none';
                        }
                    }
                }
            }
        }
    });
}
function updateDockingStatusMessage(dockingID, message) {
    const statusMessageElement = document.getElementById(`status-message-${dockingID}`);
    if (statusMessageElement) {
        statusMessageElement.textContent = message;
        statusMessageElement.style.display = message ? 'block' : 'none';
        if (message)
            autoFitText(statusMessageElement, 10, 14);
    }
}
/**
 * Restarts a cancelled docking by launching a new job with the same parameters
 * @param dockingItem - The docking item to restart
 * @param element - The DOM element to update
 */
function restartDocking(dockingItem, element) {
    return __awaiter(this, void 0, void 0, function* () {
        // Hide restart button immediately and update UI (like receptors and peptides)
        const restartBtn = element.querySelector('.btn-restart');
        if (restartBtn) {
            restartBtn.classList.remove('btn-visible');
            restartBtn.classList.add('btn-hidden');
        }
        // Show refresh and stop buttons immediately
        const refreshBtn = element.querySelector('.btn-check');
        if (refreshBtn) {
            refreshBtn.classList.remove('btn-hidden');
            refreshBtn.classList.add('btn-visible');
        }
        const stopBtn = element.querySelector('.btn-stop');
        if (stopBtn) {
            stopBtn.classList.remove('btn-hidden');
            stopBtn.classList.add('btn-visible');
        }
        // Update status immediately
        const statusSpan = element.querySelector('.peptide-status');
        if (statusSpan) {
            statusSpan.textContent = '⏳ Pending...';
            statusSpan.className = 'peptide-status pending';
        }
        // Mark element as just restarted to prevent "still in progress" toast on first refresh
        element.setAttribute('data-just-launched', 'true');
        // Hide delete button immediately (item is now processing again)
        updateDockingDeleteButtons();
        const showToast = window.showToast;
        try {
            // Relaunch the docking with the same parameters
            // This will create a new entry in DB with status pending/in_progress
            const result = yield launchDockingAPI(dockingItem.bindingSiteID, dockingItem.receptorID, dockingItem.peptideID, dockingItem.bindingSiteID === -1 ? dockingItem.customBindingSiteId : undefined);
            if (result) {
                // Update the docking item status
                dockingItem.status = 'pending';
                // Mark as restarting to prevent toasts from refreshDockingStatus
                element.setAttribute('data-restarting', 'true');
                // Refresh the docking status to update UI
                yield refreshDockingStatus(dockingItem);
                // Remove flags after refresh
                element.removeAttribute('data-just-launched');
                element.removeAttribute('data-restarting');
                // Show success toast (only one toast, replaces any previous)
                if (showToast) {
                    const bsLabel = dockingItem.bindingSiteID === -1 ? (dockingItem.customBindingSiteId || 'Custom') : String(dockingItem.bindingSiteID);
                    showToast(`Docking restarted for receptor ${dockingItem.pdbID} and binding site ${bsLabel}.`, 'success', 4000);
                }
            }
            else {
                element.removeAttribute('data-just-launched');
                if (showToast) {
                    showToast('Failed to restart docking. Please try again.', 'error', 4000);
                }
            }
        }
        catch (error) {
            console.error('Error restarting docking:', error);
            element.removeAttribute('data-just-launched');
            if (showToast) {
                showToast('Error restarting docking. Please try again.', 'error', 4000);
            }
        }
    });
}
function stopDocking(dockingID, dockingItem) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            // Show confirmation modal IMMEDIATELY (like receptor)
            const showConfirm = window.showConfirmationModal;
            if (!showConfirm) {
                alert('Confirmation modal not available. Please refresh the page.');
                return;
            }
            const confirmed = yield showConfirm('Stop Docking', 'Are you sure you want to stop this docking? The process will be stopped and cannot be resumed.', 'Stop', 'Cancel');
            if (!confirmed) {
                return;
            }
            // Send toast IMMEDIATELY after confirmation (like receptor)
            const showToast = window.showToast;
            if (showToast) {
                showToast('Docking will be stopped.', 'info', 4000);
            }
            // Mark element as stopping (prevents toasts from refreshDockingStatus)
            if (dockingItem.element) {
                dockingItem.element.setAttribute('data-stopping', 'true');
            }
            // Check status from backend FIRST to see if job is already done (before calling stop API)
            yield refreshDockingStatus(dockingItem);
            // Check if the job is already completed, failed, or cancelled
            if (dockingItem.status === 'completed') {
                // Remove flag and show our toast
                if (dockingItem.element)
                    dockingItem.element.removeAttribute('data-stopping');
                if (showToast) {
                    showToast('This docking process has already completed successfully.', 'success', 4000);
                }
                return;
            }
            if (dockingItem.status === 'failed' || dockingItem.status === 'job_submission_failed') {
                // Remove flag and show our toast
                if (dockingItem.element)
                    dockingItem.element.removeAttribute('data-stopping');
                if (showToast) {
                    const toastMessage = dockingItem.status === 'job_submission_failed'
                        ? 'This docking job submission has already failed.'
                        : 'This docking process has already failed.';
                    showToast(toastMessage, 'error', 4000);
                }
                return;
            }
            if (dockingItem.status === 'cancelled') {
                // Remove flag and show our toast
                if (dockingItem.element)
                    dockingItem.element.removeAttribute('data-stopping');
                if (showToast) {
                    showToast('This docking process has already been cancelled.', 'warning', 4000);
                }
                return;
            }
            // If still in progress, proceed with stop
            const result = yield stopDockingAPI(dockingID);
            if (result.success) {
                console.log('[STOP DOCKING] Backend returned success, setting status to cancelled locally...');
                // Set status to cancelled locally immediately (like peptides)
                // updateDockingStatus() will call updateDockingElement() which handles button visibility
                dockingItem.status = 'cancelled';
                dockingItem.statusMessage = 'Docking was cancelled';
                updateDockingStatus(dockingItem.dockingID, 'cancelled');
                // Remove flag and show our toast
                if (dockingItem.element)
                    dockingItem.element.removeAttribute('data-stopping');
                if (showToast) {
                    showToast('Docking has been cancelled successfully.', 'success', 4000);
                }
            }
            else {
                // Refresh status to get the actual current state (but no toast from refreshDockingStatus due to data-stopping)
                yield refreshDockingStatus(dockingItem);
                // Remove flag and show our toast
                if (dockingItem.element)
                    dockingItem.element.removeAttribute('data-stopping');
                if (showToast) {
                    showToast(result.message || 'Failed to stop docking. Please try again.', 'error', 4000);
                }
                else {
                    alert(result.message || 'Failed to stop docking. Please try again.');
                }
            }
        }
        catch (error) {
            console.error('Error stopping docking:', error);
            // Remove flag in case of error
            if (dockingItem.element) {
                dockingItem.element.removeAttribute('data-stopping');
            }
            const showToast = window.showToast;
            if (showToast) {
                showToast('An error occurred while stopping the docking. Please try again.', 'error', 4000);
            }
            else {
                alert('An error occurred while stopping the docking. Please try again.');
            }
        }
    });
}
export function reloadDockingData(dockingItem) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            console.log('[DEBUG] Reloading docking data for:', dockingItem.dockingID);
            const dockingsResult = yield getSessionDockings();
            const dockings = dockingsResult.dockings || [];
            // Find the updated docking data
            const updatedDocking = dockings.find((d) => d.receptorID === dockingItem.receptorID &&
                d.peptideID === dockingItem.peptideID &&
                d.bindingSiteID === dockingItem.bindingSiteID);
            if (updatedDocking) {
                console.log('[DEBUG] Found updated docking data:', updatedDocking);
                const backendStatus = updatedDocking.status;
                if (dockingItem.status === 'completed' && backendStatus !== 'completed') {
                    // Keep current status
                }
                else {
                    dockingItem.status = backendStatus || dockingItem.status || 'pending';
                }
                dockingItem.statusMessage = updatedDocking.statusMessage || '';
                dockingItem.backendDockingID = updatedDocking.dockingID;
                // Update poses if available
                if (updatedDocking.poses && Array.isArray(updatedDocking.poses)) {
                    dockingItem.poses = updatedDocking.poses;
                    console.log('[DEBUG] Updated poses count:', dockingItem.poses.length);
                }
                // Update the UI element
                updateDockingElement(dockingItem);
                updateDockingInfoButtons();
                console.log('[DEBUG] Docking data reloaded successfully');
            }
            else {
                console.warn('[DEBUG] Could not find updated docking data in backend response');
            }
        }
        catch (error) {
            console.error('[DEBUG] Error reloading docking data:', error);
        }
    });
}
function viewDockingResult(dockingItem) {
    return __awaiter(this, void 0, void 0, function* () {
        if (dockingItem.status !== 'completed') {
            console.error('Docking not completed');
            return;
        }
        try {
            // Get current user from localStorage
            const currentUser = localStorage.getItem('currentUser');
            if (!currentUser) {
                console.error('No current user found');
                return;
            }
            const userData = JSON.parse(currentUser);
            const username = userData.username;
            // Format binding site ID for path (letter for custom, padded number for predefined)
            const bindingSiteForPath = (dockingItem.bindingSiteID === -1 && dockingItem.customBindingSiteId)
                ? `b${dockingItem.customBindingSiteId}` // Custom: bA, bB, etc.
                : `b${String(dockingItem.bindingSiteID).padStart(5, '0')}`; // Predefined: b00001, b00002, etc.
            const dockingDir = `docking-p${String(dockingItem.peptideID).padStart(5, '0')}-r${String(dockingItem.receptorID).padStart(5, '0')}-${bindingSiteForPath}`;
            const baseDir = `/frontend/userSessions/dataBase/${dockingDir}`;
            const posePaths = [];
            for (let i = 1; i <= 10; i++) {
                const idx = String(i).padStart(5, '0');
                posePaths.push(`/frontend/userSessions/dataBase/${dockingDir}/pose_${idx}.pdb`);
            }
            // Show loading indicator
            const infoPanel = document.getElementById('structure-info');
            if (infoPanel) {
                infoPanel.innerHTML = '<div class="loading-indicator">🔄 Loading docking poses...</div>';
            }
            if (window.loadDockingPosesInMolstar) {
                console.log(`[Docking] Loading ${posePaths.length} poses for docking ${dockingItem.receptorID}-${dockingItem.peptideID}`);
                window.loadDockingPosesInMolstar(`docking-${dockingItem.receptorID}-${dockingItem.peptideID}`, posePaths);
            }
            else if (window.loadPdbInMolstar) {
                // Fallback to pose 1
                const fallback = `${baseDir}/poses/pose_00001.pdb`;
                console.log(`[Docking] Fallback: Loading single pose ${fallback}`);
                window.loadPdbInMolstar(`docking-${dockingItem.receptorID}-${dockingItem.peptideID}`, fallback);
            }
        }
        catch (error) {
            console.error('Error viewing docking result:', error);
        }
    });
}
function deleteDocking(dockingID, backendDockingID) {
    return __awaiter(this, void 0, void 0, function* () {
        const index = dockingList.findIndex(item => item.dockingID === dockingID);
        if (index === -1)
            return;
        // Set deletion state to prevent concurrent deletions
        isDeletingDocking = true;
        updateDockingDeleteButtons();
        updateDockingInfoButtons();
        const dockingItem = dockingList[index];
        try {
            // Delete from backend if backendDockingID exists
            const backendID = backendDockingID || dockingItem.backendDockingID;
            console.log(`[DEBUG] Deleting docking - localID: ${dockingID}, backendID: ${backendID}`);
            if (backendID) {
                const success = yield deleteSessionDocking(backendID);
                if (!success) {
                    console.error(`Failed to delete docking ${backendID} from backend`);
                    alert('Failed to delete docking from database. Please try again.');
                    // Reset deletion state on failure
                    isDeletingDocking = false;
                    updateDockingDeleteButtons();
                    updateDockingInfoButtons();
                    return; // Don't remove from UI if backend deletion failed
                }
                console.log(`Docking ${dockingID} (backend ID: ${backendID}) deleted from session`);
            }
            // Remove from UI immediately after successful backend deletion
            if (dockingItem.element) {
                dockingItem.element.remove();
            }
            // Remove from list
            dockingList.splice(index, 1);
            broadcastDockingsState();
            // Update receptor delete buttons after docking deletion
            window.dispatchEvent(new Event('dockings-updated'));
            // Remove docking poses from Mol* visualizer
            if (window.removeFromMolstar) {
                window.removeFromMolstar(`docking-${dockingItem.receptorID}-${dockingItem.peptideID}`);
            }
            // Update binding site button state
            updateBindingSiteButtonState();
            // Clear container if list is empty
            if (dockingList.length === 0) {
                const container = document.getElementById('docking-list-container');
                if (container) {
                    container.innerHTML = '';
                }
                // Switch back to step 1 if no dockings remain
                const step1Btn = document.getElementById('peptide-step-1-btn');
                const step2Btn = document.getElementById('peptide-step-2-btn');
                const step2 = document.getElementById('peptide-step-2');
                if (step1Btn && step2Btn && step2) {
                    step1Btn.classList.add('active');
                    step2Btn.classList.remove('active');
                    // Show peptide content
                    const peptideInputContainer = document.getElementById('peptide-input-container');
                    const peptideDetails = document.getElementById('peptide-details');
                    const peptideListContainer = document.getElementById('peptide-list-container');
                    if (peptideInputContainer)
                        peptideInputContainer.style.display = 'block';
                    if (peptideDetails)
                        peptideDetails.style.display = 'block';
                    if (peptideListContainer)
                        peptideListContainer.style.display = 'block';
                    // Hide docking step
                    step2.classList.add('hidden');
                }
            }
            // Reset deletion state after successful deletion
            isDeletingDocking = false;
            updateDockingDeleteButtons();
            updateDockingInfoButtons();
        }
        catch (error) {
            console.error('Error deleting docking from session:', error);
            alert('Error deleting docking. Please try again.');
            // Reset deletion state on error
            isDeletingDocking = false;
            updateDockingDeleteButtons();
            updateDockingInfoButtons();
        }
    });
}
export function updateBindingSiteButtonState() {
    // Update the main Docking tab button state
    const dockingTabButton = document.getElementById('docking-tab-button');
    if (dockingTabButton) {
        console.log(`🔍 [DEBUG] updateBindingSiteButtonState: dockingList.length = ${dockingList.length}`);
        console.log(`🔍 [DEBUG] Current docking tab classes:`, dockingTabButton.className);
        if (dockingList.length > 0) {
            dockingTabButton.classList.remove('disabled');
            dockingTabButton.title = `View docking results (${dockingList.length} docking${dockingList.length > 1 ? 's' : ''})`;
            console.log(`✅ [DEBUG] Docking tab enabled - ${dockingList.length} dockings found`);
            console.log(`✅ [DEBUG] Docking tab classes after enable:`, dockingTabButton.className);
        }
        else {
            dockingTabButton.classList.add('disabled');
            dockingTabButton.title = 'Docking results will appear here';
            console.log(`❌ [DEBUG] Docking tab disabled - no dockings found`);
            console.log(`❌ [DEBUG] Docking tab classes after disable:`, dockingTabButton.className);
        }
    }
    else {
        console.error('❌ [ERROR] Docking tab button not found');
    }
}
// Export function to be called from peptideForm when docking is launched
export function launchDocking(receptorID, peptideID, bindingSiteID, pdbID, peptideSequence) {
    return addDockingToForm(receptorID, peptideID, bindingSiteID, pdbID, peptideSequence);
}
// Export function to get the docking list for external access
export function getDockingList() {
    return dockingList;
}
export function selectDocking(dockingItem) {
    console.log('[DEBUG] selectDocking called for:', dockingItem.dockingID);
    console.log('[DEBUG] dockingItem.element exists:', !!dockingItem.element);
    // Remove selection from all other dockings
    const allDockings = document.querySelectorAll('.docking-item');
    allDockings.forEach(item => item.classList.remove('selected'));
    // Select this docking
    if (dockingItem.element) {
        dockingItem.element.classList.add('selected');
        console.log('[DEBUG] Docking element selected:', dockingItem.dockingID);
        // If docking is completed, load its results
        if (dockingItem.status === 'completed') {
            console.log('[DEBUG] Loading docking results for completed docking');
            viewDockingResult(dockingItem);
        }
        else {
            // If docking is processing, show the associated receptor
            console.log('[DEBUG] Loading receptor for processing docking');
            loadReceptorForDocking(dockingItem);
        }
    }
    else {
        console.error('[DEBUG] No element found for docking:', dockingItem.dockingID);
    }
}
export function selectDockingById(dockingID) {
    const dockingItem = findDockingByAnyId(dockingID);
    if (!(dockingItem === null || dockingItem === void 0 ? void 0 : dockingItem.element))
        return false;
    dockingItem.element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    selectDocking(dockingItem);
    return true;
}
function findDockingByAnyId(dockingID, meta) {
    const key = (dockingID || '').trim();
    if (!key && (meta === null || meta === void 0 ? void 0 : meta.receptorID) == null)
        return undefined;
    if (key) {
        const byId = dockingList.find(d => d.dockingID === key || d.backendDockingID === key);
        if (byId)
            return byId;
        // UI list number (« check docking #1 ») — 1-based, same order as the panel.
        if (/^\d+$/.test(key)) {
            const idx = parseInt(key, 10) - 1;
            if (idx >= 0 && idx < dockingList.length)
                return dockingList[idx];
        }
    }
    if ((meta === null || meta === void 0 ? void 0 : meta.receptorID) != null && (meta === null || meta === void 0 ? void 0 : meta.peptideID) != null) {
        const matches = dockingList.filter(d => Number(d.receptorID) === Number(meta.receptorID) &&
            Number(d.peptideID) === Number(meta.peptideID));
        if (meta.bindingSiteID != null) {
            const bySite = matches.find(d => Number(d.bindingSiteID) === Number(meta.bindingSiteID));
            if (bySite)
                return bySite;
        }
        if (matches.length === 1)
            return matches[0];
        if (matches.length > 1)
            return matches[matches.length - 1];
    }
    return undefined;
}
/** Chat agent: ensure docking row exists, optionally check (same as manual Check/Launch).
 *
 * reason:
 *   - 'check'  → call check-dock API + progress bar (explicit « check docking » / panel Check)
 *   - 'launch' → call check once after agent launch (same as new row from Launch)
 *   - 'focus'  → select only — NO check, NO progress bar (reconnect / soft focus)
 */
export function syncDockingFromAgent(dockingID, meta) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a, _b, _c, _d, _e, _f, _g;
        const reason = (_a = meta === null || meta === void 0 ? void 0 : meta.reason) !== null && _a !== void 0 ? _a : 'focus';
        const shouldCheck = reason === 'check' || reason === 'launch';
        const applyDisplayMeta = (dockingItem) => {
            if ((meta === null || meta === void 0 ? void 0 : meta.pdbID) && (!dockingItem.pdbID || dockingItem.pdbID === 'Unknown')) {
                dockingItem.pdbID = meta.pdbID;
            }
            if ((meta === null || meta === void 0 ? void 0 : meta.peptideSequence) &&
                (!dockingItem.peptideSequence || dockingItem.peptideSequence === 'No sequence available')) {
                dockingItem.peptideSequence = meta.peptideSequence;
            }
            // Progress / statusMessage only when explicitly checking (not on reconnect focus).
            if (reason === 'check') {
                if (meta === null || meta === void 0 ? void 0 : meta.statusMessage) {
                    dockingItem.statusMessage = meta.statusMessage;
                }
                if (meta === null || meta === void 0 ? void 0 : meta.dockingStatus) {
                    const s = meta.dockingStatus;
                    if (['pending', 'in_progress', 'completed', 'failed', 'job_submission_failed', 'cancelled'].includes(s)) {
                        dockingItem.status = s;
                    }
                }
                if (dockingItem.element)
                    updateDockingElement(dockingItem);
            }
            else if (dockingItem.element && ((meta === null || meta === void 0 ? void 0 : meta.pdbID) || (meta === null || meta === void 0 ? void 0 : meta.peptideSequence))) {
                updateDockingElement(dockingItem);
            }
        };
        const selectAfter = (dockingItem) => {
            if (dockingItem.element) {
                dockingItem.element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                selectDocking(dockingItem);
            }
        };
        let dockingItem = findDockingByAnyId(dockingID, meta);
        if (dockingItem) {
            applyDisplayMeta(dockingItem);
            if (shouldCheck) {
                if (reason === 'launch') {
                    (_b = dockingItem.element) === null || _b === void 0 ? void 0 : _b.setAttribute('data-just-launched', 'true');
                }
                yield refreshDockingStatus(dockingItem);
                (_c = dockingItem.element) === null || _c === void 0 ? void 0 : _c.removeAttribute('data-just-launched');
                if (reason === 'check' && (meta === null || meta === void 0 ? void 0 : meta.statusMessage)) {
                    dockingItem.statusMessage = meta.statusMessage;
                    if (dockingItem.element)
                        updateDockingElement(dockingItem);
                }
            }
            selectAfter(dockingItem);
            return true;
        }
        const [dockData, receptorsResult, peptidesResult] = yield Promise.all([
            getSessionDockings(),
            getSessionReceptors(),
            getSessionPeptides(),
        ]);
        let d = (_d = dockData.dockings) === null || _d === void 0 ? void 0 : _d.find(x => x.dockingID === dockingID);
        if (!d && /^\d+$/.test(dockingID.trim())) {
            const idx = parseInt(dockingID.trim(), 10) - 1;
            const sorted = [...(dockData.dockings || [])].sort((a, b) => new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime());
            d = sorted[idx];
        }
        if (!d && (meta === null || meta === void 0 ? void 0 : meta.receptorID) != null && (meta === null || meta === void 0 ? void 0 : meta.peptideID) != null) {
            d = (_e = dockData.dockings) === null || _e === void 0 ? void 0 : _e.find(x => Number(x.receptorID) === Number(meta.receptorID) &&
                Number(x.peptideID) === Number(meta.peptideID));
        }
        if (!d) {
            console.warn('[syncDockingFromAgent] Docking not found in session:', dockingID);
            return false;
        }
        const receptorMap = new Map((receptorsResult.receptors || []).map(r => [Number(r.receptorID), r]));
        const peptideMap = new Map((peptidesResult.peptides || []).map(p => [Number(p.peptideID), p]));
        const receptor = receptorMap.get(Number(d.receptorID));
        const peptide = peptideMap.get(Number(d.peptideID));
        addDockingToForm(d.receptorID, d.peptideID, d.bindingSiteID, (meta === null || meta === void 0 ? void 0 : meta.pdbID) || d.pdbID || (receptor === null || receptor === void 0 ? void 0 : receptor.pdbID) || 'Unknown', (meta === null || meta === void 0 ? void 0 : meta.peptideSequence) || d.peptideSequence || (peptide === null || peptide === void 0 ? void 0 : peptide.peptideSequence) || 'No sequence available', d.status, d.dockingID);
        dockingItem = findDockingByAnyId(d.dockingID, {
            receptorID: d.receptorID,
            peptideID: d.peptideID,
            bindingSiteID: d.bindingSiteID,
        });
        if (!dockingItem)
            return true;
        applyDisplayMeta(dockingItem);
        if (shouldCheck) {
            if (reason === 'launch') {
                (_f = dockingItem.element) === null || _f === void 0 ? void 0 : _f.setAttribute('data-just-launched', 'true');
            }
            yield refreshDockingStatus(dockingItem);
            (_g = dockingItem.element) === null || _g === void 0 ? void 0 : _g.removeAttribute('data-just-launched');
            if (reason === 'check' && (meta === null || meta === void 0 ? void 0 : meta.statusMessage)) {
                dockingItem.statusMessage = meta.statusMessage;
                if (dockingItem.element)
                    updateDockingElement(dockingItem);
            }
        }
        selectAfter(dockingItem);
        return true;
    });
}
window.syncDockingFromAgent = syncDockingFromAgent;
// Load receptor associated with a docking (during processing)
function loadReceptorForDocking(dockingItem) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            console.log(`[Docking] Loading receptor for docking ${dockingItem.dockingID} (receptorID: ${dockingItem.receptorID})`);
            // Get current user from localStorage
            const currentUser = localStorage.getItem('currentUser');
            if (!currentUser) {
                console.error('No current user found');
                return;
            }
            const userData = JSON.parse(currentUser);
            const username = userData.username;
            const receptorPath = `/frontend/userSessions/dataBase/receptor-${String(dockingItem.receptorID).padStart(5, '0')}/rec.pdb`;
            // Show loading indicator
            const infoPanel = document.getElementById('structure-info');
            if (infoPanel) {
                infoPanel.innerHTML = '<div class="loading-indicator">🔄 Loading receptor for docking...</div>';
            }
            // Load receptor in Mol*
            if (window.loadPdbInMolstar) {
                console.log(`[Docking] Loading receptor ${dockingItem.pdbID} for docking ${dockingItem.dockingID}`);
                window.loadPdbInMolstar(dockingItem.pdbID, receptorPath);
            }
            else {
                console.error('loadPdbInMolstar function not available');
            }
        }
        catch (error) {
            console.error('Error loading receptor for docking:', error);
        }
    });
}
// No renumbering needed - items are sorted by creation date from backend
// Height of list is handled in CSS
// selectDocking function moved to exports section above
// Shrinks text size so content fits inside its box without overflow
function autoFitText(el, minPx = 10, maxPx = 14) {
    // Allow wrapping & word breaking for compact fit
    el.style.whiteSpace = 'normal';
    el.style.wordBreak = 'break-word';
    el.style.overflowWrap = 'anywhere';
    el.style.overflow = 'hidden';
    // Start from current or max
    let size = parseInt(getComputedStyle(el).fontSize || `${maxPx}`, 10);
    if (isNaN(size))
        size = maxPx;
    size = Math.min(size, maxPx);
    const fits = () => el.scrollHeight <= el.clientHeight && el.scrollWidth <= el.clientWidth;
    // Reduce font size until it fits or we reach min
    while (!fits() && size > minPx) {
        size -= 1;
        el.style.fontSize = `${size}px`;
    }
}
// ===========================
// DOCKING INFO MODAL FUNCTIONS
// ===========================
function showDockingInfoModal(dockingItem) {
    return __awaiter(this, void 0, void 0, function* () {
        console.log('📊 [DEBUG] Opening docking info modal for:', dockingItem);
        const modal = document.getElementById('info-modal');
        const title = document.getElementById('info-modal-title');
        const body = document.getElementById('info-modal-body');
        if (!modal || !title || !body) {
            console.error('❌ [ERROR] Modal elements not found');
            return;
        }
        // Set title
        title.textContent = 'Docking Results';
        // Add special class for docking results modal
        modal.classList.add('docking-results');
        // Ensure correct button visibility
        const headerExportBtn = document.getElementById('info-modal-export-header');
        if (headerExportBtn) {
            headerExportBtn.style.display = 'none';
        }
        // Remove any existing download icon to prevent duplicates
        const existingDownloadIcon = document.getElementById('docking-download-icon');
        if (existingDownloadIcon) {
            existingDownloadIcon.remove();
        }
        // Get current user ID from localStorage or session
        const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
        const userId = currentUser.username || 'unknown';
        const currentDate = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
        // Show loading state
        body.innerHTML = '<div class="loading-indicator">🔄 Loading docking results...</div>';
        try {
            // Use docking data from the item itself (same approach as peptide info modal)
            const dockingData = dockingItem;
            console.log('📊 [DEBUG] Using docking data from item:', dockingData);
            console.log('🔍 [DEBUG] Available keys in dockingData:', Object.keys(dockingData));
            console.log('🔍 [DEBUG] Poses in item:', dockingData.poses);
            // Create compact info section - simple and clean
            const compactInfo = `
      <div class="docking-info-compact">
        <div class="info-row">
          <span class="info-item">
            <span class="info-label">Receptor:</span>
            <span class="info-value">${dockingItem.pdbID}</span>
          </span>
          <span class="info-item">
            <span class="info-label">Binding Site:</span>
            <span class="info-value">${dockingItem.bindingSiteID === -1 ? (dockingItem.customBindingSiteId || 'Custom') : dockingItem.bindingSiteID}</span>
          </span>
        </div>
        <div class="info-row">
          <span class="info-item">
            <span class="info-label">Peptide:</span>
            <span class="info-value">${dockingItem.peptideSequence}</span>
          </span>
        </div>
      </div>
    `;
            // Use poses data directly from the item (already parsed)
            const poses = dockingData.poses || [];
            // Sort poses by binding affinity (best first - more negative is better)
            const sortedPoses = poses.sort((a, b) => a.bindingAffinity - b.bindingAffinity);
            const poseRows = sortedPoses.map((pose, index) => {
                const rank = index + 1;
                const rankText = rank === 1 ? 'Best' :
                    rank === 2 ? '2nd' :
                        rank === 3 ? '3rd' :
                            `${rank}th`;
                const rankColor = rank === 1 ? 'var(--green-dark)' : 'var(--blue-dark)';
                const affinityColor = rank === 1 ? 'var(--green-dark)' : 'var(--blue-dark)';
                return `
        <tr>
          <td>${pose.poseId}</td>
          <td style="color: ${affinityColor}; font-weight: 600;">${pose.bindingAffinity.toFixed(1)}</td>
          <td><span style="background: ${rankColor}; color: white; padding: 2px 8px; border-radius: 12px; font-size: 11px;">${rankText}</span></td>
        </tr>
      `;
            }).join('');
            // Create table content with real data
            const tableContent = `
      ${compactInfo}
      <div class="table-container">
        <table class="info-table compact">
            <thead>
              <tr>
                <th>ID</th>
                <th>Affinity</th>
                <th>Rank</th>
              </tr>
            </thead>
            <tbody>
              ${poseRows || '<tr><td colspan="3" style="text-align: center; color: #666;">No poses available</td></tr>'}
            </tbody>
          </table>
      </div>
    `;
            body.innerHTML = tableContent;
            // Add download icon to header for docking results
            const headerActions = modal.querySelector('.modal-header-actions');
            if (headerActions) {
                const downloadIcon = document.createElement('button');
                downloadIcon.id = 'docking-download-icon';
                downloadIcon.className = 'btn-export-icon';
                downloadIcon.innerHTML = `
        <svg class="download-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
          <polyline points="7,10 12,15 17,10"/>
          <line x1="12" y1="15" x2="12" y2="3"/>
        </svg>
      `;
                downloadIcon.onclick = () => {
                    window.exportDockingCSV(dockingItem.dockingID.toString(), userId, currentDate);
                };
                // Insert before the close button
                headerActions.insertBefore(downloadIcon, headerActions.querySelector('.modal-close'));
            }
            // Force modal width calculation based on screen size
            const modalContent = modal.querySelector('.modal-content');
            if (modalContent) {
                const screenWidth = window.innerWidth;
                if (screenWidth <= 1024) {
                    modalContent.style.width = '25vw';
                }
                else if (screenWidth <= 1200) {
                    modalContent.style.width = '480px';
                }
                else if (screenWidth <= 1280) {
                    modalContent.style.width = '420px';
                }
                else if (screenWidth >= 2000) {
                    modalContent.style.width = '560px';
                }
                else if (screenWidth >= 1600) {
                    modalContent.style.width = '520px';
                }
                else {
                    modalContent.style.width = '480px';
                }
            }
            // Show modal
            modal.classList.remove('hidden');
        }
        catch (error) {
            console.error('❌ [ERROR] Failed to load docking data:', error);
            body.innerHTML = `
      <div style="text-align: center; padding: 40px; color: #666;">
        <div style="font-size: 48px; margin-bottom: 16px;">⚠️</div>
        <h3 style="color: #333; margin-bottom: 12px; font-weight: 500;">Unable to load results</h3>
        <p style="margin-bottom: 8px; font-size: 14px;">The docking data could not be retrieved.</p>
        <p style="margin-bottom: 24px; font-size: 14px;">Please try again later.</p>
      </div>
    `;
            modal.classList.remove('hidden');
        }
        // Button visibility already handled at the beginning of the function
        // Setup close handlers (reuse from peptide form)
        setupDockingModalCloseHandlers();
    });
}
function setupDockingModalCloseHandlers() {
    const modal = document.getElementById('info-modal');
    const closeBtn = document.getElementById('info-modal-close');
    if (!modal || !closeBtn)
        return;
    // Remove all existing event listeners
    modal.onclick = null;
    closeBtn.onclick = null;
    // Only close on close button click
    closeBtn.onclick = () => {
        modal.classList.add('hidden');
        modal.classList.remove('docking-results');
        // Hide export icon button for docking results
        const exportIconBtn = document.getElementById('info-modal-export-icon');
        if (exportIconBtn) {
            exportIconBtn.style.display = 'none';
        }
        // Show header export button for other modals
        const headerExportBtn = document.getElementById('info-modal-export-header');
        if (headerExportBtn) {
            headerExportBtn.style.display = 'flex';
        }
        // Ensure docking tab state is correct after closing modal
        updateBindingSiteButtonState();
    };
    // Close on Escape key
    const handleEscape = (e) => {
        if (e.key === 'Escape') {
            modal.classList.add('hidden');
            modal.classList.remove('docking-results');
            // Hide export icon button for docking results
            const exportIconBtn = document.getElementById('info-modal-export-icon');
            if (exportIconBtn) {
                exportIconBtn.style.display = 'none';
            }
            // Show header export button for other modals
            const headerExportBtn = document.getElementById('info-modal-export-header');
            if (headerExportBtn) {
                headerExportBtn.style.display = 'flex';
            }
            // Ensure docking tab state is correct after closing modal
            updateBindingSiteButtonState();
            document.removeEventListener('keydown', handleEscape);
        }
    };
    document.addEventListener('keydown', handleEscape);
    console.log('🔍 [DEBUG] Docking modal close handlers setup - only close button and Escape');
}
function downloadDockingPDB(dockingItem) {
    return __awaiter(this, void 0, void 0, function* () {
        console.log('📥 [DEBUG] Downloading PDB for docking:', dockingItem.dockingID);
        try {
            // Get current user from localStorage
            const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
            const userId = currentUser.username || 'unknown';
            const username = userId;
            const currentDate = new Date().toISOString().split('T')[0];
            // Format binding site ID for path (letter for custom, padded number for predefined)
            const bindingSiteForPath = (dockingItem.bindingSiteID === -1 && dockingItem.customBindingSiteId)
                ? `b${dockingItem.customBindingSiteId}` // Custom: bA, bB, etc.
                : `b${String(dockingItem.bindingSiteID).padStart(5, '0')}`; // Predefined: b00001, b00002, etc.
            // Docking directory name
            const dockingDir = `docking-p${String(dockingItem.peptideID).padStart(5, '0')}-r${String(dockingItem.receptorID).padStart(5, '0')}-${bindingSiteForPath}`;
            // Get authentication token from localStorage (same as Mol*)
            const authToken = localStorage.getItem('accessToken');
            // Adapt paths based on execution mode (same as Mol*)
            const getAdaptedPath = (filePath) => {
                const JOB_EXECUTION_MODE = process.env.JOB_EXECUTION_MODE || 'local';
                const adapted = JOB_EXECUTION_MODE === 'ecs'
                    ? filePath.replace('/userSessions/', '/mnt/data/userSessions/') // EFS path
                    : filePath; // Local path
                const separator = adapted.includes('?') ? '&' : '?';
                return `${adapted}${separator}sessionID=${encodeURIComponent(resolveCurrentSessionId())}`;
            };
            // Build pose paths (same as Mol*)
            const posePaths = [];
            for (let i = 1; i <= 10; i++) {
                const idx = String(i).padStart(5, '0');
                posePaths.push(`/frontend/userSessions/dataBase/${dockingDir}/pose_${idx}.pdb`);
            }
            // Adapt paths based on execution mode (same as Mol*)
            const adaptedPaths = posePaths.map(path => getAdaptedPath(path));
            // Check file existence first (HEAD requests are faster) - same logic as Mol*
            console.log('🔍 [DEBUG] Checking pose file availability...');
            const existingPoses = [];
            const checkPromises = adaptedPaths.map((path, index) => __awaiter(this, void 0, void 0, function* () {
                const url = `${BACKEND_URL}${path}`;
                try {
                    const res = yield fetch(url, {
                        method: 'HEAD',
                        headers: authToken ? {
                            'Authorization': `Bearer ${authToken}`
                        } : {}
                    });
                    if (res.ok) {
                        existingPoses.push(path);
                        return { path, index, exists: true };
                    }
                }
                catch (e) {
                    console.warn(`[DEBUG] Pose ${index + 1} not available:`, path);
                }
                return { path, index, exists: false };
            }));
            const checkResults = yield Promise.all(checkPromises);
            const availablePoses = checkResults.filter(r => r.exists);
            if (availablePoses.length === 0) {
                throw new Error('No PDB files found');
            }
            console.log(`✅ [SUCCESS] Found ${availablePoses.length} PDB files`);
            try {
                // Create a ZIP with all available poses
                const JSZip = window.JSZip;
                if (JSZip) {
                    const zip = new JSZip();
                    // Fetch all available poses and add to ZIP (same as Mol*)
                    for (const poseInfo of availablePoses) {
                        try {
                            const url = `${BACKEND_URL}${poseInfo.path}`;
                            const poseResponse = yield fetch(url, {
                                headers: authToken ? {
                                    'Authorization': `Bearer ${authToken}`
                                } : {}
                            });
                            if (poseResponse.ok) {
                                const pdbContent = yield poseResponse.text();
                                const fileName = poseInfo.path.split('/').pop(); // Extract filename
                                zip.file(fileName || `pose_${poseInfo.index + 1}.pdb`, pdbContent);
                                console.log(`✅ Added ${fileName} to ZIP`);
                            }
                            else {
                                console.warn(`❌ Could not fetch ${poseInfo.path}`);
                            }
                        }
                        catch (err) {
                            console.warn(`❌ Error fetching ${poseInfo.path}:`, err);
                        }
                    }
                    // Generate and download ZIP
                    const zipBlob = yield zip.generateAsync({ type: 'blob' });
                    const link = document.createElement('a');
                    const filename = `${dockingItem.dockingID}_poses_${userId}_${currentDate}.zip`;
                    link.href = URL.createObjectURL(zipBlob);
                    link.download = filename;
                    link.style.display = 'none';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    console.log('✅ [SUCCESS] ZIP downloaded:', filename);
                }
                else {
                    // Fallback: download first available pose (same as Mol*)
                    if (availablePoses.length > 0) {
                        const firstPose = availablePoses[0];
                        const url = `${BACKEND_URL}${firstPose.path}`;
                        const poseResponse = yield fetch(url, {
                            headers: authToken ? {
                                'Authorization': `Bearer ${authToken}`
                            } : {}
                        });
                        if (poseResponse.ok) {
                            const pdbContent = yield poseResponse.text();
                            const fileName = firstPose.path.split('/').pop();
                            const blob = new Blob([pdbContent], { type: 'text/plain;charset=utf-8;' });
                            const link = document.createElement('a');
                            const filename = `${dockingItem.dockingID}_${fileName}_${userId}_${currentDate}.pdb`;
                            link.href = URL.createObjectURL(blob);
                            link.download = filename;
                            link.style.display = 'none';
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            console.log('✅ [SUCCESS] PDB downloaded:', filename);
                        }
                        else {
                            throw new Error('Could not download PDB file');
                        }
                    }
                    else {
                        throw new Error('No PDB files available');
                    }
                }
            }
            catch (error) {
                // If PDB files don't exist, show message
                console.log('❌ [ERROR] PDB files not found:', error);
                alert(`PDB files are not yet available. The docking may still be processing.\n\nPlease check if the docking is completed and try again.`);
            }
        }
        catch (error) {
            console.error('❌ [ERROR] Failed to download PDB:', error);
            alert('Failed to download PDB file. Please try again.');
        }
    });
}
// Global function for CSV export (accessible from HTML onclick)
window.exportDockingCSV = function (dockingID, userId, date) {
    return __awaiter(this, void 0, void 0, function* () {
        console.log('📄 [DEBUG] Exporting docking CSV for:', { dockingID, userId, date });
        // Get the docking data from the local docking list
        const dockingItem = dockingList.find(d => d.dockingID === dockingID);
        if (!dockingItem) {
            console.error('❌ [ERROR] Docking data not found for CSV export');
            return;
        }
        try {
            // Use docking data from the item itself (same approach as peptide info modal)
            const dockingData = dockingItem;
            // Use poses data directly from the item (already parsed)
            const poses = dockingData.poses || [];
            // Sort poses by binding affinity (best first - more negative is better)
            const sortedPoses = poses.sort((a, b) => a.bindingAffinity - b.bindingAffinity);
            // Create CSV content with real data
            const rows = [
                ['Receptor PDB ID', dockingItem.pdbID],
                ['Binding Site', dockingItem.bindingSiteID.toString()],
                ['Peptide Sequence', dockingItem.peptideSequence],
                ['', ''],
                ['Pose ID', 'Binding Affinity (kcal/mol)', 'Rank']
            ];
            // Add pose data rows
            sortedPoses.forEach((pose, index) => {
                const rank = index + 1;
                const rankText = rank === 1 ? 'Best' :
                    rank === 2 ? '2nd' :
                        rank === 3 ? '3rd' :
                            `${rank}th`;
                rows.push([
                    pose.poseId.toString(),
                    pose.bindingAffinity.toFixed(1),
                    rankText
                ]);
            });
            // If no poses available, add a placeholder row
            if (poses.length === 0) {
                rows.push(['', 'No poses available', '']);
            }
            const csvContent = rows.map(row => row.map(field => `"${field}"`).join(',')).join('\n');
            // Create and download file
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const filename = `${dockingID}_${userId}_${date}.csv`;
            link.href = URL.createObjectURL(blob);
            link.download = filename;
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            console.log('✅ [SUCCESS] CSV exported:', filename);
        }
        catch (error) {
            console.error('❌ [ERROR] Failed to export CSV:', error);
            alert('Failed to export CSV. Please try again.');
        }
    });
};
// Shrinks a single-line label to fit horizontally without overflow
function autoFitTextWidth(el, minPx = 9, maxPx = 13) {
    el.style.whiteSpace = 'nowrap';
    el.style.overflow = 'hidden';
    el.style.textOverflow = 'clip';
    let size = parseInt(getComputedStyle(el).fontSize || `${maxPx}`, 10);
    if (isNaN(size))
        size = maxPx;
    size = Math.min(size, maxPx);
    const fits = () => el.scrollWidth <= el.clientWidth;
    while (!fits() && size > minPx) {
        size -= 1;
        el.style.fontSize = `${size}px`;
    }
}
